import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { OnlyIdRequiredInterface } from '@zaoblako/shared/common/interfaces/only-id-required.interface';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import { ContragentService } from '@zaoblako/web/core/services/client/contragent.service';
import { AutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/auto-complete/auto-complete.component';
import { HighlightMatchTextHelper } from '@zaoblako/web/shared/helpers/highlight-match-text.helper';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';

@Component({
  standalone: true,
  selector: 'crm-contragent-auto-complete',
  templateUrl: './contragent-auto-complete.component.html',
  imports: [CommonModule, AutoCompleteComponent, SafeHtmlPipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContragentAutoCompleteComponent implements OnChanges {
  @ViewChild('counterpartyAutoComplete', { static: true }) public counterpartyAutoComplete: AutoCompleteComponent;

  @Input() public selectedContragent: ContragentFindInterface | OnlyIdRequiredInterface<ContragentFindInterface>;
  @Output() public selectedContragentChange = new EventEmitter<ContragentFindInterface>();

  @Input() public contragents: ContragentFindInterface[] = [];
  @Input() public isError = false;

  @Input() public class: string;
  @Input() public styleClass: string | string[] | Set<string> | { [klass: string]: any };
  @Input() public inputStyle: any;
  @Input() public inputStyleClass: string;
  @Input() public placeholder: string;
  @Input() public disabled = false;
  @Input() public appendTo: string;
  @Input() public showClear = false;

  @Input() public excludeIds: number[] = [];
  @Input() public clientId: number;
  @Input() public withBankDetails = false;
  @Input() public withClients = false;
  @Input() public withExternalUsers = false;
  @Input() public onlyWithSalaryControl = false;

  @Input() public customSearch = true;
  @Output() public contragentSearch = new EventEmitter<string>();

  public search: string;

  public constructor(
    private readonly contragentService: ContragentService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    if ((changes.excludeIds || changes.onlyWithSalaryControl) && !this.customSearch) {
      this.searchContragent(this.search);
    }
  }

  public toContragent(contragent: any): ContragentFindInterface {
    return contragent as ContragentFindInterface;
  }

  public searchContragent(name: string) {
    this.search = name;
    const select: (keyof ContragentFindInterface)[] = ['id', 'name', 'inn', 'kpp', 'personType'];
    if (this.withBankDetails) {
      select.push('bankDetails');
    }
    if (this.withClients) {
      select.push('client');
    }
    if (this.withExternalUsers) {
      select.push('externalUsers');
    }
    if (this.onlyWithSalaryControl) {
      select.push('salaryAmount', 'isSalaryControl');
    }

    this.contragentService
      .getAll({
        limit: 50,
        offset: 0,
        search: name,
        select,
        clientIds: this.clientId ? [this.clientId] : [],
        excludeIds: this.excludeIds?.length ? this.excludeIds : [],
        onlyWithSalaryControl: this.onlyWithSalaryControl || undefined
      })
      .subscribe((data) => {
        this.contragents = data.items;
        this.cdRef.markForCheck();
      });
  }

  public highlightMatchText(text: string): string {
    return HighlightMatchTextHelper.highlightMatchText(text, this.counterpartyAutoComplete?.autoComplete?.inputValue());
  }
}
